import React, { useState } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function ConfirmDeleteModal({ 
    isOpen, 
    title = '¿Eliminar elemento?', 
    description = 'Esta acción no se puede deshacer.',
    onConfirm, 
    onCancel,
    isLoading = false
}) {
    const [message, setMessage] = useState('');
    const MIN_LENGTH = 10;
    const MAX_LENGTH = 200;
    
    const isMessageValid = message.length >= MIN_LENGTH && message.length <= MAX_LENGTH;

    const handleConfirm = () => {
        if (isMessageValid) {
            onConfirm(message);
            setMessage('');
        }
    };

    const handleCancel = () => {
        setMessage('');
        onCancel?.();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fadeIn">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <TrashIcon className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-lg font-bold text-white">{title}</h2>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="text-white hover:bg-white/20 p-1 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-700 text-sm mb-5">{description}</p>

                    {/* Campo de mensaje */}
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                            Confirma escribiendo un mensaje (mín. {MIN_LENGTH} caracteres)
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                            placeholder={`Escribe un mensaje de al menos ${MIN_LENGTH} caracteres...`}
                            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all resize-none uppercase ${
                                message.length === 0
                                    ? 'border-gray-200 focus:border-red-500 focus:ring-red-200'
                                    : message.length < MIN_LENGTH
                                    ? 'border-yellow-400 focus:border-yellow-500 focus:ring-yellow-200'
                                    : 'border-emerald-500 focus:border-emerald-600 focus:ring-emerald-200'
                            }`}
                            rows="3"
                        />
                        <div className="flex justify-between items-center mt-2">
                            <p className={`text-xs font-semibold ${
                                message.length < MIN_LENGTH 
                                    ? 'text-yellow-600' 
                                    : 'text-emerald-600'
                            }`}>
                                {message.length}/{MAX_LENGTH} caracteres
                            </p>
                            {message.length < MIN_LENGTH && message.length > 0 && (
                                <p className="text-xs text-yellow-600">
                                    Faltan {MIN_LENGTH - message.length} caracteres
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Alert */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
                        <p className="text-xs text-red-700">
                            <span className="font-bold">⚠️ Advertencia:</span> Está a punto de eliminar un registro. Esta acción no se puede deshacer.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
                    <button
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!isMessageValid || isLoading}
                        className={`px-6 py-2 font-bold rounded-lg transition-all flex items-center gap-2 ${
                            isMessageValid && !isLoading
                                ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                        }`}
                    >
                        <TrashIcon className="w-4 h-4" />
                        {isLoading ? 'Eliminando...' : 'Eliminar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
